import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import type { Row } from "../types";
import ErrorState from "./ErrorState";
import EventoCentral from "./EventoCentral";
import EventosCalendario from "./EventosCalendario";
import { tomDoStatus } from "./EventoPecas";
import { fmt, norm } from "../endomarketing-utils";
import {
  IconSearch,
  IconPlus,
  IconTrash,
  IconClose,
  IconUpload,
  IconEdit,
  IconDownload,
  IconStar,
} from "../icons";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome } from "../ordem";

/**
 * EVENTOS DO ANO — o acervo de fotos da empresa (confraternização, treinamento,
 * campanha, entrega de EPI…). Cada evento é uma pasta de imagens em disco.
 *
 * O álbum tem visualizador em tela cheia (com ←/→ e Esc), legenda por foto,
 * escolha da CAPA e download — sem isso o acervo vira um monte de arquivo que
 * ninguém abre.
 */

interface Evento {
  id: number;
  nome: string;
  data: string | null;
  local: string | null;
  descricao: string | null;
  fotos: number;
  ano: number | null;
  capa: string | null;
  /* O que a CENTRAL acrescentou. Opcionais de propósito: se a listagem cair na
     consulta simples (reserva do servidor), a tela continua funcionando. */
  tipo?: string | null;
  status?: string | null;
  data_fim?: string | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  dia_inteiro?: boolean;
  cidade?: string | null;
  estado?: string | null;
  responsavel_nome?: string | null;
  atividades?: number;
  participantes?: number;
  confirmados?: number;
  checklist_total?: number;
  checklist_feitas?: number;
  tags?: string[];
}

interface Foto {
  id: number;
  arquivo: string;
  legenda: string | null;
  enviado_em: string;
  tamanho: number;
  disponivel: boolean;
  url: string;
  /** Foto de iPhone (HEIC) virando JPEG no servidor — ver server/heic.ts. */
  convertendo?: boolean;
  falhou_conversao?: boolean;
}

export default function Eventos({ onAviso }: { onAviso: (m: string) => void }) {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [ano, setAno] = useState<string>("");
  const [form, setForm] = useState<Evento | "novo" | null>(null);
  const [aberto, setAberto] = useState<Evento | null>(null);
  /** Evento aberto na CENTRAL (a tela completa de planejamento). */
  const [central, setCentral] = useState<number | null>(null);
  /** Situação escolhida na barra ("" = todas). */
  const [status, setStatus] = useState("");
  /** Como a lista é mostrada: cards ou calendário do mês. */
  const [visao, setVisao] = useState<"lista" | "calendario">("lista");
  /** Lembretes que vencem hoje (aviso no topo da aba). */
  const [lembretes, setLembretes] = useState<any[]>([]);
  /**
   * O cadastro do RH, usado pela central para escolher responsável, equipe e
   * participantes — ninguém é recadastrado dentro do evento.
   */
  const [colaboradores, setColaboradores] = useState<Row[]>([]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setEventos(await api.endomarketing.eventos());
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Best-effort: sem a lista, a central ainda abre (só não sugere gente).
    api.endomarketing
      .fotos()
      // Em ordem alfabética: é a lista dos selects de responsável/equipe/participantes.
      .then((f) => setColaboradores(ordenarPorNome((f.colaboradores ?? []) as Row[], (c) => c.nome)))
      .catch(() => setColaboradores([]));
  }, []);

  /**
   * Lembretes que vencem hoje. O envio automático (e-mail/WhatsApp) ainda não
   * existe — então o lembrete aparece AQUI, que é onde quem cuida dos eventos
   * já está olhando. Melhor um aviso na tela do que um registro esperando uma
   * infraestrutura que não chegou.
   */
  const carregarLembretes = useCallback(() => {
    api.endomarketing
      .lembretesDeHoje()
      .then((l) => setLembretes(l ?? []))
      .catch(() => setLembretes([]));
  }, []);

  useEffect(() => {
    carregarLembretes();
  }, [carregarLembretes]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const anos = useMemo(
    () => [...new Set(eventos.map((e) => e.ano).filter(Boolean))].sort((a, b) => b! - a!) as number[],
    [eventos],
  );

  const lista = useMemo(() => {
    const q = norm(busca);
    const hoje = new Date().toISOString().slice(0, 10);
    return eventos.filter((e) => {
      if (ano && String(e.ano) !== ano) return false;
      // "Próximos" e "Passados" saem da DATA; o resto é a situação escolhida.
      if (status === "__proximos__" && !(e.data && e.data >= hoje)) return false;
      if (status === "__passados__" && !(e.data && e.data < hoje)) return false;
      if (status && !status.startsWith("__") && (e.status ?? "") !== status) return false;
      if (!q) return true;
      return (
        norm(e.nome).includes(q) ||
        norm(e.local).includes(q) ||
        norm(e.descricao).includes(q) ||
        norm(e.cidade).includes(q) ||
        (e.tags ?? []).some((t) => norm(t).includes(q))
      );
    });
  }, [eventos, busca, ano, status]);

  const totalFotos = lista.reduce((n, e) => n + e.fotos, 0);

  // O álbum aberto precisa acompanhar a lista (contagem de fotos, capa nova).
  const eventoAberto = aberto ? (eventos.find((e) => e.id === aberto.id) ?? aberto) : null;

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  return (
    <>
      <div className="crud-toolbar endo-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar evento, local ou descrição..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="endo-mes">
          <span className="muted">Situação</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="__proximos__">Próximos</option>
            <option value="__passados__">Já realizados</option>
            <option value="Planejamento">Planejamento</option>
            <option value="Confirmado">Confirmado</option>
            <option value="Em andamento">Em andamento</option>
            <option value="Concluído">Concluído</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </label>
        <label className="endo-mes">
          <span className="muted">Ano</span>
          <select value={ano} onChange={(e) => setAno(e.target.value)}>
            <option value="">Todos</option>
            {anos.map((a) => (
              <option key={a} value={String(a)}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <div className="toolbar-right">
          {/* Lista × Calendário: a mesma lista filtrada, vista de dois jeitos. */}
          <div className="evc-troca">
            <button
              type="button"
              className={visao === "lista" ? "ativa" : ""}
              onClick={() => setVisao("lista")}
            >
              Lista
            </button>
            <button
              type="button"
              className={visao === "calendario" ? "ativa" : ""}
              onClick={() => setVisao("calendario")}
            >
              Calendário
            </button>
          </div>
          <span className="badge">{lista.length} evento(s)</span>
          <span className="badge">{totalFotos} foto(s)</span>
          <button className="btn-primary" onClick={() => setForm("novo")}>
            <IconPlus size={17} /> Novo evento
          </button>
        </div>
      </div>

      {lembretes.length > 0 && (
        <div className="ev-lembretes">
          {lembretes.map((l) => (
            <div key={l.id} className="ev-lembrete">
              <strong>
                {l.faltam === 0
                  ? "É hoje:"
                  : l.faltam === 1
                    ? "É amanhã:"
                    : `Faltam ${l.faltam} dias:`}
              </strong>
              <button type="button" className="link-btn" onClick={() => setCentral(l.evento_id)}>
                {l.evento}
              </button>
              {l.mensagem && <small className="muted">{l.mensagem}</small>}
              <button
                type="button"
                className="btn-secondary btn-sm"
                title="Já vi este lembrete"
                onClick={async () => {
                  await api.endomarketing.dispensarLembrete(l.id).catch(() => {});
                  carregarLembretes();
                }}
              >
                Ok, já vi
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : visao === "calendario" ? (
        <EventosCalendario eventos={lista} onAbrir={(id) => setCentral(id)} />
      ) : lista.length === 0 ? (
        <div className="empty">
          {eventos.length === 0
            ? "Nenhum evento ainda. Crie um (confraternização, treinamento, campanha) e envie as fotos."
            : "Nenhum evento com esses filtros."}
        </div>
      ) : (
        <div className="endo-eventos">
          {lista.map((e) => (
            <article key={e.id} className="endo-evento">
              <button
                type="button"
                className="endo-evento-capa"
                onClick={() => setAberto(e)}
                title="Abrir o álbum"
              >
                {e.capa ? (
                  <img
                    src={`/api/endomarketing/eventos/${e.id}/fotos/${encodeURIComponent(e.capa)}`}
                    alt={e.nome}
                    loading="lazy"
                  />
                ) : (
                  <span>sem fotos</span>
                )}
                <span className="endo-evento-qtd">{e.fotos} foto(s)</span>
              </button>
              <div className="endo-evento-info">
                <strong>{e.nome}</strong>
                <small className="muted">
                  {fmt(e.data)}
                  {e.local ? ` · ${e.local}` : ""}
                </small>
                {/* O que a central acrescentou: situação, período e os números
                    do planejamento. Vem da mesma listagem — sem chamada extra. */}
                <div className="endo-evento-selos">
                  {e.status && <span className={`pill ${tomDoStatus(e.status)}`}>{e.status}</span>}
                  {e.tipo && <span className="pill pill-neutral">{e.tipo}</span>}
                </div>
                {((e.atividades ?? 0) > 0 ||
                  (e.participantes ?? 0) > 0 ||
                  (e.checklist_total ?? 0) > 0) && (
                  <small className="muted endo-evento-numeros">
                    {(e.atividades ?? 0) > 0 && `${e.atividades} atividade(s)`}
                    {(e.participantes ?? 0) > 0 && ` · ${e.participantes} participante(s)`}
                    {(e.checklist_total ?? 0) > 0 &&
                      ` · checklist ${e.checklist_feitas ?? 0}/${e.checklist_total}`}
                  </small>
                )}
                {e.descricao && <p className="endo-evento-desc">{e.descricao}</p>}
                <div className="endo-evento-acoes">
                  {/* A CENTRAL é a ação principal agora; o álbum continua ali. */}
                  <button className="btn-primary btn-sm" onClick={() => setCentral(e.id)}>
                    Abrir evento
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => setAberto(e)}>
                    Álbum
                  </button>
                  <button className="icon-btn" title="Editar nome, data e local" onClick={() => setForm(e)}>
                    <IconEdit size={16} />
                  </button>
                  {podeExcluir && (
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Excluir evento"
                      onClick={async () => {
                        if (!confirm(`Excluir "${e.nome}" e todas as ${e.fotos} foto(s)?`)) return;
                        await api.endomarketing.removerEvento(e.id);
                        onAviso(`Evento "${e.nome}" excluído.`);
                        await carregar();
                      }}
                    >
                      <IconTrash size={16} />
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {form && (
        <EventoForm
          evento={form === "novo" ? null : form}
          onClose={() => setForm(null)}
          onSaved={async () => {
            setForm(null);
            await carregar();
          }}
        />
      )}

      {eventoAberto && (
        <AlbumModal
          evento={eventoAberto}
          onClose={() => setAberto(null)}
          onMudou={carregar}
          onAviso={onAviso}
        />
      )}

      {central != null && (
        <EventoCentral
          eventoId={central}
          colaboradores={colaboradores}
          usuario={localStorage.getItem("sercamp-user") ?? undefined}
          onClose={() => setCentral(null)}
          onMudou={async (msg) => {
            onAviso(msg);
            await carregar();
          }}
        />
      )}
    </>
  );
}

/* ==================== Formulário (criar / editar) ==================== */

function EventoForm({
  evento,
  onClose,
  onSaved,
}: {
  evento: Evento | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nome, setNome] = useState(evento?.nome ?? "");
  const [data, setData] = useState(evento?.data?.slice(0, 10) ?? "");
  const [local, setLocal] = useState(evento?.local ?? "");
  const [descricao, setDescricao] = useState(evento?.descricao ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <form
        className="modal-card"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!nome.trim()) return setErro("Informe o nome do evento.");
          setSalvando(true);
          try {
            const dados = { nome: nome.trim(), data, local, descricao };
            if (evento) await api.endomarketing.atualizarEvento(evento.id, dados);
            else await api.endomarketing.criarEvento(dados);
            onSaved();
          } catch (err) {
            setErro(err instanceof ApiError ? err.message : "Erro ao salvar.");
            setSalvando(false);
          }
        }}
      >
        <div className="modal-head">
          <h3>{evento ? `Editar — ${evento.nome}` : "Novo evento"}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label className="field field-wide">
              <span>
                Nome <em className="req">*</em>
              </span>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Confraternização 2026"
                autoFocus
              />
            </label>
            <label className="field">
              <span>Data</span>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </label>
            <label className="field">
              <span>Local</span>
              <input
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder="Ex.: Sede — Campinas"
              />
            </label>
            <label className="field field-wide">
              <span>Descrição</span>
              <textarea rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
            </label>
          </div>
          {erro && <div className="form-error">{erro}</div>}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={salvando}>
            {salvando ? "Salvando…" : evento ? "Salvar" : "Criar evento"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ==================== Álbum ==================== */

function AlbumModal({
  evento,
  onClose,
  onMudou,
  onAviso,
}: {
  evento: Evento;
  onClose: () => void;
  onMudou: () => Promise<void>;
  onAviso: (m: string) => void;
}) {
  const podeExcluir = usePodeExcluir(); // apagar foto do álbum tira o arquivo do disco
  const [fotos, setFotos] = useState<Foto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [visor, setVisor] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setCarregando(true);
      try {
        setFotos(await api.endomarketing.fotosEvento(evento.id));
      } finally {
        if (!silencioso) setCarregando(false);
      }
    },
    [evento.id],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Fotos de iPhone (HEIC) viram JPEG no servidor, uma por vez: enquanto houver
  // alguma na fila, a lista se atualiza sozinha e cada foto aparece ao ficar
  // pronta. Quando a última termina, a capa do card também é recarregada.
  const temConvertendo = fotos.some((f) => f.convertendo);
  const convertiaAntes = useRef(false);
  useEffect(() => {
    if (!temConvertendo) {
      if (convertiaAntes.current) {
        convertiaAntes.current = false;
        onMudou().catch(() => {});
      }
      return;
    }
    convertiaAntes.current = true;
    const t = setTimeout(() => carregar(true).catch(() => {}), 4000);
    return () => clearTimeout(t);
  }, [temConvertendo, fotos, carregar, onMudou]);

  // Esc fecha o visor (e, sem visor aberto, o álbum); setas navegam.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return visor === null ? onClose() : setVisor(null);
      if (visor === null) return;
      if (e.key === "ArrowRight") setVisor((i) => (i === null ? null : (i + 1) % fotos.length));
      if (e.key === "ArrowLeft")
        setVisor((i) => (i === null ? null : (i - 1 + fotos.length) % fotos.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visor, fotos.length, onClose]);

  async function enviar(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    setEnviando(true);
    try {
      const r = await api.endomarketing.enviarFotosEvento(evento.id, files);
      onAviso(`${r.salvos} foto(s) enviada(s) para "${evento.nome}".`);
      await carregar();
      await onMudou();
    } catch (e) {
      onAviso(e instanceof Error ? e.message : "Erro ao enviar as fotos.");
    } finally {
      setEnviando(false);
    }
  }

  const atual = visor !== null ? fotos[visor] : null;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-card modal-album"
        onMouseDown={(e) => e.stopPropagation()}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastando(false);
          enviar(e.dataTransfer.files);
        }}
      >
        <div className="modal-head">
          <div>
            <h3>{evento.nome}</h3>
            <p className="modal-sub">
              {fmt(evento.data)}
              {evento.local ? ` · ${evento.local}` : ""} · {fotos.length} foto(s)
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className={`modal-body ${arrastando ? "endo-soltar" : ""}`}>
          <div className="endo-album-topo">
            <button
              className="btn-primary btn-sm"
              onClick={() => inputRef.current?.click()}
              disabled={enviando}
            >
              <IconUpload size={15} /> {enviando ? "Enviando…" : "Adicionar fotos"}
            </button>
            <span className="muted">ou arraste as imagens para cá</span>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                enviar(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {carregando ? (
            <div className="empty">Carregando…</div>
          ) : fotos.length === 0 ? (
            <div className="empty">Nenhuma foto ainda. Use "Adicionar fotos".</div>
          ) : (
            <div className="endo-album">
              {fotos.map((f, i) => (
                <figure key={f.id} className={evento.capa === f.arquivo ? "capa" : ""}>
                  <button
                    type="button"
                    className="endo-album-img"
                    onClick={() => f.disponivel && setVisor(i)}
                    disabled={!f.disponivel}
                  >
                    {f.disponivel ? (
                      <img src={f.url} alt={f.legenda ?? f.arquivo} loading="lazy" />
                    ) : f.convertendo ? (
                      <span className="endo-foto-sumiu endo-foto-convertendo">
                        <i aria-hidden="true" /> convertendo foto do iPhone…
                      </span>
                    ) : f.falhou_conversao ? (
                      <span className="endo-foto-sumiu">
                        não consegui converter esta foto do iPhone — envie em JPG
                      </span>
                    ) : (
                      <span className="endo-foto-sumiu">arquivo não encontrado</span>
                    )}
                  </button>
                  <figcaption>
                    <span title={f.legenda || f.arquivo}>{f.legenda || f.arquivo}</span>
                    <button
                      className={`icon-btn ${evento.capa === f.arquivo ? "icon-btn-ok" : ""}`}
                      title="Usar como capa do evento"
                      onClick={async () => {
                        await api.endomarketing.atualizarFotoEvento(evento.id, f.arquivo, { capa: true });
                        await onMudou();
                      }}
                    >
                      <IconStar size={15} />
                    </button>
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir foto"
                        onClick={async () => {
                          if (!confirm("Excluir esta foto?")) return;
                          await api.endomarketing.removerFotoEvento(evento.id, f.arquivo);
                          await carregar();
                          await onMudou();
                        }}
                      >
                        <IconTrash size={15} />
                      </button>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span className="muted">
            Máximo 15 MB por imagem · jpg, png, webp, gif, heic (iPhone — vira JPG sozinho)
          </span>
          <button type="button" className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>

      {atual && (
        <Visor
          foto={atual}
          indice={visor!}
          total={fotos.length}
          onIr={(d) => setVisor((i) => (i === null ? null : (i + d + fotos.length) % fotos.length))}
          onFechar={() => setVisor(null)}
          onLegenda={async (legenda) => {
            await api.endomarketing.atualizarFotoEvento(evento.id, atual.arquivo, { legenda });
            await carregar();
          }}
        />
      )}
    </div>
  );
}

/** Visualizador em tela cheia: navega com ←/→, edita a legenda e baixa. */
function Visor({
  foto,
  indice,
  total,
  onIr,
  onFechar,
  onLegenda,
}: {
  foto: Foto;
  indice: number;
  total: number;
  onIr: (delta: number) => void;
  onFechar: () => void;
  onLegenda: (legenda: string) => Promise<void>;
}) {
  const [legenda, setLegenda] = useState(foto.legenda ?? "");
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    setLegenda(foto.legenda ?? "");
    setSalvo(false);
  }, [foto.id, foto.legenda]);

  return (
    <div className="endo-visor" onMouseDown={onFechar}>
      <button className="endo-visor-fechar icon-btn" onClick={onFechar} title="Fechar (Esc)">
        <IconClose size={22} />
      </button>

      <button
        className="endo-visor-nav esquerda"
        onMouseDown={(e) => {
          e.stopPropagation();
          onIr(-1);
        }}
        title="Anterior (←)"
      >
        ‹
      </button>

      <figure className="endo-visor-palco" onMouseDown={(e) => e.stopPropagation()}>
        <img src={foto.url} alt={foto.legenda ?? foto.arquivo} />
        <figcaption>
          <span className="muted">
            {indice + 1} de {total} · {foto.arquivo}
            {foto.tamanho ? ` · ${(foto.tamanho / 1024).toFixed(0)} KB` : ""}
          </span>
          <div className="endo-visor-legenda">
            <input
              value={legenda}
              placeholder="Legenda da foto (aparece no álbum)"
              onChange={(e) => {
                setLegenda(e.target.value);
                setSalvo(false);
              }}
            />
            <button
              className="btn-secondary btn-sm"
              onClick={async () => {
                await onLegenda(legenda);
                setSalvo(true);
              }}
            >
              {salvo ? "Salva!" : "Salvar legenda"}
            </button>
            <a className="btn-secondary btn-sm" href={foto.url} download={foto.arquivo}>
              <IconDownload size={15} /> Baixar
            </a>
          </div>
        </figcaption>
      </figure>

      <button
        className="endo-visor-nav direita"
        onMouseDown={(e) => {
          e.stopPropagation();
          onIr(1);
        }}
        title="Próxima (→)"
      >
        ›
      </button>
    </div>
  );
}
